import React from "react";
import type { TokenMarketData } from "@crypto-trading/common/browser";

/** $1.57M, $124.46K, $12.3B. */
export function compactUsd(value: number | null): string {
  if (value == null) return "--";
  return `$${compactNumber(value)}`;
}

export function compactNumber(value: number | null): string {
  if (value == null) return "--";
  const abs = Math.abs(value);
  const [div, unit] =
    abs >= 1e12 ? [1e12, "T"] : abs >= 1e9 ? [1e9, "B"] : abs >= 1e6 ? [1e6, "M"] : abs >= 1e3 ? [1e3, "K"] : [1, ""];
  return `${(value / div).toLocaleString("en-US", { maximumFractionDigits: 2 })}${unit}`;
}

/** Market Cap, FDV, 24h Volume / Total, Circulating, Max supply (CoinMarketCap). */
export const TokenMarketStats: React.FC<{ market: TokenMarketData; symbol: string }> = ({ market, symbol }) => {
  const supply = (value: number | null) => (value == null ? "--" : `${compactNumber(value)} ${symbol}`);
  const cells: Array<{ label: string; value: string; note?: React.ReactNode }> = [
    { label: "Market Cap", value: compactUsd(market.marketCap) },
    { label: "Fully Diluted Valuation", value: compactUsd(market.fullyDilutedValuation) },
    {
      label: "24h Volume",
      value: compactUsd(market.volume24h),
      note:
        market.volumeChange24h != null ? (
          <span className={market.volumeChange24h >= 0 ? "text-[#2dcc89]" : "text-[#f97066]"}>
            {market.volumeChange24h >= 0 ? "+" : ""}
            {market.volumeChange24h.toFixed(2)}%
          </span>
        ) : undefined,
    },
    { label: "Total Supply", value: supply(market.totalSupply) },
    { label: "Circulating Supply", value: supply(market.circulatingSupply) },
    { label: "Max Supply", value: supply(market.maxSupply) },
  ];
  return (
    <div className="glass grid grid-cols-3 overflow-hidden !rounded-xl">
      {cells.map((cell, i) => (
        <div
          key={cell.label}
          className={`flex min-w-0 flex-col gap-0.5 p-3 ${i % 3 ? "border-l border-white/10" : ""} ${i >= 3 ? "border-t border-white/10" : ""}`}
        >
          <span className="truncate text-xs text-[#a3a3a3]">{cell.label}</span>
          <span className="truncate text-sm font-semibold text-white">{cell.value}</span>
          {cell.note && <span className="text-[10px]">{cell.note}</span>}
        </div>
      ))}
    </div>
  );
};
